import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type WebsiteTheme = 'system' | 'light' | 'dark';
export type ResolvedWebsiteTheme = Exclude<WebsiteTheme, 'system'>;

type WebsiteThemeContextValue = {
  theme: WebsiteTheme;
  resolvedTheme: ResolvedWebsiteTheme;
  setTheme: React.Dispatch<React.SetStateAction<WebsiteTheme>>;
};

const WEBSITE_THEME_STORAGE_KEY = 'cueboard.website.theme';
const SYSTEM_DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

const WebsiteThemeContext = createContext<WebsiteThemeContextValue | null>(
  null
);

function isWebsiteTheme(value: string | null): value is WebsiteTheme {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function getSystemResolvedTheme(): ResolvedWebsiteTheme {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia(SYSTEM_DARK_MEDIA_QUERY).matches
  ) {
    return 'dark';
  }

  return 'light';
}

export function resolveWebsiteTheme(theme: WebsiteTheme): ResolvedWebsiteTheme {
  return theme === 'system' ? getSystemResolvedTheme() : theme;
}

export function getStoredWebsiteTheme(): WebsiteTheme {
  if (typeof window === 'undefined') {
    return 'system';
  }

  try {
    const storedTheme = window.localStorage.getItem(WEBSITE_THEME_STORAGE_KEY);
    return isWebsiteTheme(storedTheme) ? storedTheme : 'system';
  } catch {
    return 'system';
  }
}

function applyWebsiteThemeClass(theme: ResolvedWebsiteTheme) {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.dataset.websiteTheme = theme;
}

export function applyWebsiteTheme(theme: WebsiteTheme) {
  applyWebsiteThemeClass(resolveWebsiteTheme(theme));
}

export function WebsiteThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [theme, setTheme] = useState<WebsiteTheme>(() =>
    getStoredWebsiteTheme()
  );
  const [systemResolvedTheme, setSystemResolvedTheme] =
    useState<ResolvedWebsiteTheme>(() => getSystemResolvedTheme());

  const resolvedTheme =
    theme === 'system' ? systemResolvedTheme : (theme as ResolvedWebsiteTheme);

  useEffect(() => {
    applyWebsiteThemeClass(resolvedTheme);

    try {
      window.localStorage.setItem(WEBSITE_THEME_STORAGE_KEY, theme);
    } catch {
      // Ignore storage failures so theme changes still apply for this session.
    }
  }, [resolvedTheme, theme]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(SYSTEM_DARK_MEDIA_QUERY);
    const handleChange = () => {
      setSystemResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const contextValue = useMemo<WebsiteThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
    }),
    [resolvedTheme, theme]
  );

  return (
    <WebsiteThemeContext.Provider value={contextValue}>
      {children}
    </WebsiteThemeContext.Provider>
  );
}

export function useWebsiteTheme() {
  const context = useContext(WebsiteThemeContext);

  if (!context) {
    throw new Error('useWebsiteTheme must be used within WebsiteThemeProvider');
  }

  return context;
}
